import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { FakeLink } from '@hivebrite/design-system/Link';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

const isEnabled = ({
  response_type: response,
  user_total_tickets: userTotalTickets,
}) => response === 'attend' || userTotalTickets > 0;

const EventAttendeeVisibility = () => {
  const { event, updateEvent } = useEventContext();
  const { currentUser } = useDefaultAppContexts();
  const queryClient = useQueryClient();

  const [isLoading, setLoading] = useState(false);

  const { id: userId } = currentUser;

  const {
    id: eventId,
    response_visibility: isVisible,
    participation_type: participationType,
  } = event;

  const linkPath = useMemo(() => {
    const isRsvpEvent = participationType === 'rsvp';
    const isTicketsEvent = participationType === 'tickets';

    if (isRsvpEvent) {
      const route = isVisible
        ? Routes.visibility_opt_out_user_network_event_responses_path
        : Routes.visibility_opt_in_user_network_event_responses_path;

      return route(userId, eventId);
    }

    if (isTicketsEvent) {
      const route = isVisible
        ? Routes.visibility_opt_out_event_attendees_path
        : Routes.visibility_opt_in_event_attendees_path;

      return route(eventId);
    }
  }, [userId, eventId, isVisible, participationType]);

  const linkText = isVisible
    ? __('Hide me from attendee list')
    : __('Show me in attendee list');

  const confirmationText = isVisible
    ? __('You are no longer be visible in the attendees lists')
    : __('You are visible in the attendees lists');

  const updateVisibility = async () => {
    setLoading(true);

    try {
      const { data } = await axios.put(linkPath);

      displayNotif('success', confirmationText);
      queryClient.invalidateQueries({
        queryKey: ['event', 'attendees', event.type, event.id],
      });
      updateEvent(data.event || data.event_customized);
    } catch (_) {
      displayNotif(
        'error',
        __('An error has occurred, please try again later.'),
      );
    }

    setLoading(false);
  };

  if (
    !currentUser ||
    !isEnabled(event) ||
    !event.show_participants ||
    !event.current_user_is_event_attendee
  ) {
    return null;
  }

  return (
    <FakeLink
      as="span"
      aria-busy={isLoading}
      onClick={updateVisibility}
      data-testid={isVisible ? 'visibility_opt_out' : 'visibility_opt_in'}
    >
      {linkText}
    </FakeLink>
  );
};

export default EventAttendeeVisibility;
